import { formulaToSegments } from './chemicalEquation';
import { DIAGRAM_BOX, ELEMENT_NAMES, type PlacedAtom, type PlacedBond } from './matterClassifier';

const ELEMENT_STYLES: Record<string, { fill: string; text: string }> = {
  H: { fill: 'fill-canvas-pure stroke-ink/50', text: 'fill-ink' },
  He: { fill: 'fill-brand-sunshine stroke-ink/40', text: 'fill-ink' },
  C: { fill: 'fill-ink stroke-ink', text: 'fill-canvas-pure' },
  N: { fill: 'fill-brand-sky stroke-ink/40', text: 'fill-ink' },
  O: { fill: 'fill-brand-coral stroke-ink/40', text: 'fill-ink' },
  Na: { fill: 'fill-brand-bubblegum stroke-ink/40', text: 'fill-ink' },
  Cl: { fill: 'fill-brand-mint stroke-ink/40', text: 'fill-ink' },
  Ar: { fill: 'fill-brand-bubblegum stroke-ink/40', text: 'fill-ink' },
  Cu: { fill: 'fill-stone2 stroke-ink/40', text: 'fill-ink' },
};
const FALLBACK_STYLE = { fill: 'fill-surface stroke-ink/40', text: 'fill-ink' };
const BOND_GAP = 2.4;

function styleFor(element: string) {
  return ELEMENT_STYLES[element] ?? FALLBACK_STYLE;
}

function BondLines({ bond }: { bond: PlacedBond }) {
  const length = Math.hypot(bond.x2 - bond.x1, bond.y2 - bond.y1) || 1;
  const normalX = -(bond.y2 - bond.y1) / length;
  const normalY = (bond.x2 - bond.x1) / length;
  const offsets = bond.order === 1 ? [0] : bond.order === 2 ? [-0.5, 0.5] : [-1, 0, 1];
  return (
    <>
      {offsets.map((offset) => (
        <line
          key={offset}
          x1={bond.x1 + normalX * offset * BOND_GAP}
          y1={bond.y1 + normalY * offset * BOND_GAP}
          x2={bond.x2 + normalX * offset * BOND_GAP}
          y2={bond.y2 + normalY * offset * BOND_GAP}
          className="stroke-slate2"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      ))}
    </>
  );
}

/** Ball-and-stick or space-filling picture drawn only from placed atom data. */
export function MoleculeDiagram({
  atoms,
  bonds,
  label,
  atomRadius = 7,
}: {
  atoms: PlacedAtom[];
  bonds: PlacedBond[];
  label: string;
  atomRadius?: number;
}) {
  return (
    <svg
      viewBox={`0 0 ${DIAGRAM_BOX.width} ${DIAGRAM_BOX.height}`}
      role="img"
      aria-label={label}
      className="w-full rounded-2xl border border-brand-sky/25 bg-wash-sky"
    >
      {bonds.map((bond) => (
        <BondLines key={bond.key} bond={bond} />
      ))}
      {atoms.map((placed) => {
        const style = styleFor(placed.element);
        return (
          <g key={placed.key}>
            <circle cx={placed.x} cy={placed.y} r={atomRadius} className={style.fill} strokeWidth="0.8" />
            <text
              x={placed.x}
              y={placed.y + atomRadius * 0.38}
              textAnchor="middle"
              fontSize={atomRadius * (placed.element.length > 1 ? 0.95 : 1.1)}
              fontWeight="800"
              className={style.text}
            >
              {placed.element}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export function ElementLegend({ elements }: { elements: string[] }) {
  return (
    <ul className="flex flex-wrap gap-2" aria-label="Key to atoms">
      {elements.map((element) => (
        <li key={element} className="inline-flex items-center gap-2 rounded-full border border-hairline bg-canvas-pure px-3 py-1 text-xs font-bold text-ink-soft">
          <svg viewBox="0 0 20 20" className="h-5 w-5" aria-hidden="true">
            <circle cx="10" cy="10" r="8.5" className={styleFor(element).fill} strokeWidth="1" />
            <text x="10" y="13.5" textAnchor="middle" fontSize={element.length > 1 ? 8 : 10} fontWeight="800" className={styleFor(element).text}>
              {element}
            </text>
          </svg>
          {ELEMENT_NAMES[element] ?? element}
        </li>
      ))}
    </ul>
  );
}

/** Formula with chemically correct subscripts (digits after a symbol or bracket). */
export function FormulaText({ formula }: { formula: string }) {
  return (
    <span className="whitespace-nowrap">
      {formulaToSegments(formula).map((segment, index) =>
        segment.subscript ? <sub key={index}>{segment.text}</sub> : <span key={index}>{segment.text}</span>,
      )}
    </span>
  );
}
